import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "../App";
import { toast } from "sonner";
import VideoPlayer from "./VideoPlayer";
import MindMap from "./MindMap";
import Flashcards from "./Flashcards";
import Quiz from "./Quiz";
import Slides from "./Slides";
import Infographic from "./Infographic";
import ArtifactProgressCard from "./ArtifactProgressCard";
import FloatingProcessingIndicator from "./FloatingProcessingIndicator";
import { 
  ArrowLeft,
  PlayCircle,
  CheckCircle2,
  Clock,
  ExternalLink,
  BookOpen,
  Award,
  ChevronRight,
  ChevronDown,
  Download,
  Network,
  CreditCard,
  ClipboardCheck,
  Presentation,
  BarChart3,
  Sparkles,
  RotateCcw
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CourseViewer = () => {
  const { courseId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSegment, setActiveSegment] = useState(null);
  const [expandedSegments, setExpandedSegments] = useState(new Set());
  const [activeTab, setActiveTab] = useState("video");
  const [videoArtifacts, setVideoArtifacts] = useState(null);
  const [videoData, setVideoData] = useState(null); // Dados do vídeo (com transcrição)
  const [isRegeneratingArtifacts, setIsRegeneratingArtifacts] = useState(false);
  const [regeneratingArtifact, setRegeneratingArtifact] = useState(null); // Qual artefato está sendo regenerado
  const [processingQueue, setProcessingQueue] = useState([]); // Fila de artefatos aguardando processamento
  const [showFloatingIndicator, setShowFloatingIndicator] = useState(false); // Mostrar indicador flutuante
  const pollingTimeoutRef = React.useRef(null); // Para cancelar polling

  useEffect(() => {
    if (token && courseId) {
      fetchCourseData();
    }
    
    // Cleanup ao desmontar componente
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
        console.log("[Cleanup] CourseViewer desmontou, polling cancelado");
      }
    };
  }, [token, courseId]);

  // Reset states when active segment changes (FASE 2: carregar artefatos da API)
  useEffect(() => {
    // Cleanup: cancelar polling anterior se existir
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
      console.log("[Cleanup] Polling anterior cancelado");
    }
    
    if (activeSegment) {
      // Reset tab to video when changing segment
      setActiveTab("video");
      
      // Carregar dados do vídeo local se existir
      if (activeSegment.video_id) {
        loadVideoData(activeSegment.video_id);
      } else {
        setVideoData(null);
      }
      
      // FASE 2: Verificar se já existem artefatos (NÃO gerar automaticamente)
      checkVideoArtifacts(activeSegment);
    }
  }, [activeSegment]);

  // Determinar se a URL do vídeo é do YouTube
  const isYouTubeUrl = activeSegment?.video_url && (
    activeSegment.video_url.includes('youtube.com') || 
    activeSegment.video_url.includes('youtu.be')
  );

  // Função para carregar dados do vídeo (incluindo descrição gerada por IA)
  const loadVideoData = async (videoId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      console.log(`[Video] Carregando dados do vídeo: ${videoId}`);
      
      const response = await axios.get(
        `${API}/videos/${videoId}`,
        { headers }
      );
      
      const data = response.data;
      
      // Se tem descrição gerada por IA, usar; caso contrário usar transcrição
      console.log(`[Video] Descrição gerada:`, data.description?.substring(0, 100));
      
      setVideoData({
        ...data,
        transcription: {
          transcription_text: data.transcription_text,
          transcription_segments: data.transcription_segments,
          language: data.language,
          model_used: data.model_used
        }
      });
    } catch (error) {
      console.error("[Video] Erro ao carregar dados:", error);
      setVideoData(null);
    }
  };

  // Função para verificar se artefatos já existem (SEM gerar automaticamente)
  const checkVideoArtifacts = async (segment) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      try {
        console.log(`[Artefatos] Verificando se já existem para ${segment.id}...`);
        const existingResponse = await axios.get(
          `${API}/video-artifacts/${segment.id}`,
          { headers }
        );
        
        const existing = existingResponse.data;
        setVideoArtifacts(existing);
        
      } catch (checkError) {
        // 404 = não existem ainda, mostrar estado vazio
        if (checkError.response?.status === 404) {
          console.log("[Artefatos] Não existem ainda.");
          setVideoArtifacts(null);
        } else {
          console.error("[Artefatos] Erro ao verificar:", checkError);
          setVideoArtifacts(null);
        }
      }
      
    } catch (error) {
      console.error("[Artefatos] Erro geral:", error);
      setVideoArtifacts(null);
    }
  };

  // Função para gerar um artefato específico quando não existe
  const generateSpecificArtifactIfNotExists = async (artifactType) => {
    if (!activeSegment) return;
    
    try {
      // Se já está processando outro artefato, enfileirar este
      if (regeneratingArtifact) {
        console.log(`[Fila] ${artifactType} enfileirado. Aguardando conclusão de ${regeneratingArtifact}`);
        setProcessingQueue(prev => [...prev, artifactType]);
        setShowFloatingIndicator(true);
        toast.info(`${artifactType} foi adicionado à fila. Aguardando conclusão do processamento atual...`);
        return;
      }
      
      setRegeneratingArtifact(artifactType);
      setShowFloatingIndicator(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      console.log(`[Gerar] Iniciando geração de ${artifactType}`);
      
      // Buscar dados do vídeo se for local
      let transcriptionText = '';
      if (activeSegment.video_id) {
        const videoResponse = await axios.get(
          `${API}/videos/${activeSegment.video_id}`,
          { headers }
        );
        transcriptionText = videoResponse.data.transcription?.transcription_text || '';
      }
      
      // Gerar artefato específico
      const requestData = {
        segment_id: activeSegment.id,
        video_url: activeSegment.video_url || null,
        transcription_text: transcriptionText,
        title: activeSegment.title,  // Adicionar título para infográfico
        force: false, // Não forçar, apenas gerar se não existir
        artifacts_to_generate: [artifactType]
      };
      
      console.log(`[Gerar] Request data para ${artifactType}:`, JSON.stringify(requestData, null, 2));
      
      await axios.post(
        `${API}/video-artifacts/generate`,
        requestData,
        { 
          headers: {
            ...headers,
            'Content-Type': 'application/json'
          }
        }
      );
      
      toast.success(`Gerando ${artifactType}...`);
      
      // Esperar um pouco antes de começar o polling (dar tempo para o backend processar)
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Polling para aguardar conclusão
      let pollCount = 0;
      const maxPolls = 80;
      
      const pollArtifact = async () => {
        if (pollCount >= maxPolls) {
          console.error(`[Gerar] Timeout ao gerar ${artifactType}`);
          toast.error("Timeout ao gerar artefato");
          setRegeneratingArtifact(null);
          return;
        }
        
        pollCount++;
        
        try {
          const response = await axios.get(
            `${API}/video-artifacts/${activeSegment.id}`,
            { headers }
          );
          
          const artifacts = response.data;
          
          if (artifacts.status === "PENDING") {
            console.log(`[Gerar] Polling ${pollCount}/${maxPolls}`);
            setTimeout(pollArtifact, 3000);
          } else {
            setVideoArtifacts(artifacts);
            if (artifacts.status === "COMPLETED") {
              console.log(`[Gerar] ✅ ${artifactType} gerado!`);
              toast.success(`${artifactType} gerado com sucesso!`);
            } else if (artifacts.status === "ERROR") {
              console.error(`[Gerar] Erro: ${artifacts.error_message}`);
              toast.error(`Erro: ${artifacts.error_message}`);
            }
            setRegeneratingArtifact(null);
            
            // Processar próximo item da fila
            processNextInQueue();
          }
        } catch (error) {
          console.error(`[Gerar] Erro no polling:`, error);
          if (pollCount < maxPolls) {
            setTimeout(pollArtifact, 3000);
          } else {
            toast.error("Erro ao gerar artefato");
            setRegeneratingArtifact(null);
          }
        }
      };
      
      pollArtifact();
      
    } catch (error) {
      console.error(`Erro ao gerar ${artifactType}:`, error);
      toast.error(`Erro ao gerar ${artifactType}`);
      setRegeneratingArtifact(null);
      
      // Tentar processar próximo da fila
      processNextInQueue();
    }
  };

  // Processar próximo item da fila
  const processNextInQueue = async () => {
    setProcessingQueue(prev => {
      if (prev.length === 0) {
        setShowFloatingIndicator(false);
        return prev;
      }
      
      const [nextArtifact, ...rest] = prev;
      console.log(`[Fila] Processando próximo: ${nextArtifact}`);
      
      // Usar timeout para garantir que o state foi atualizado
      setTimeout(() => {
        generateSpecificArtifactIfNotExists(nextArtifact);
      }, 100);
      
      return rest;
    });
  };

  // Função para regenerar artefato específico
  const regenerateSpecificArtifact = async (artifactType) => {
    if (!activeSegment) return;
    
    try {
      setRegeneratingArtifact(artifactType);
      const headers = { Authorization: `Bearer ${token}` };
      
      console.log(`[Regenerar V2] Iniciando regeneração de ${artifactType}`);
      
      // Buscar dados do vídeo se for local
      let transcriptionText = '';
      if (activeSegment.video_id) {
        const videoResponse = await axios.get(
          `${API}/videos/${activeSegment.video_id}`,
          { headers }
        );
        transcriptionText = videoResponse.data.transcription?.transcription_text || '';
      }
      
      // Forçar regeneração do artefato específico
      const requestData = {
        segment_id: activeSegment.id,
        video_url: activeSegment.video_url || null,
        transcription_text: transcriptionText,
        title: activeSegment.title,  // Adicionar título para infográfico
        force: true,
        artifacts_to_generate: [artifactType]
      };
      
      console.log(`[Regenerar V2] Request data:`, JSON.stringify(requestData, null, 2));
      
      await axios.post(
        `${API}/video-artifacts/generate`,
        requestData,
        { 
          headers: {
            ...headers,
            'Content-Type': 'application/json'
          }
        }
      );
      
      toast.success(`Regenerando ${artifactType}...`);
      
      // Polling para aguardar conclusão
      let pollCount = 0;
      const maxPolls = 40;
      
      const pollArtifact = async () => {
        if (pollCount >= maxPolls) {
          console.error(`[Regenerar] Timeout ao regenerar ${artifactType}`);
          toast.error("Timeout ao regenerar artefato");
          setRegeneratingArtifact(null);
          return;
        }
        
        pollCount++;
        console.log(`[Regenerar] Polling ${pollCount}/${maxPolls} para ${artifactType}`);
        
        try {
          const response = await axios.get(
            `${API}/video-artifacts/${activeSegment.id}`,
            { headers }
          );
          
          const artifacts = response.data;
          console.log(`[Regenerar] Status: ${artifacts.status}`);
          
          if (artifacts.status === "PENDING") {
            setTimeout(pollArtifact, 3000);
          } else {
            setVideoArtifacts(artifacts);
            if (artifacts.status === "COMPLETED") {
              console.log(`[Regenerar] ${artifactType} regenerado com sucesso!`);
              toast.success(`${artifactType} regenerado com sucesso!`);
            } else if (artifacts.status === "ERROR") {
              console.error(`[Regenerar] Erro: ${artifacts.error_message}`);
              toast.error(`Erro: ${artifacts.error_message}`);
            }
            setRegeneratingArtifact(null);
          }
        } catch (error) {
          console.error(`[Regenerar] Erro no polling:`, error);
          if (error.response?.status === 404 && pollCount < maxPolls) {
            setTimeout(pollArtifact, 3000);
          } else if (pollCount < maxPolls) {
            setTimeout(pollArtifact, 3000);
          } else {
            toast.error("Erro ao regenerar artefato");
            setRegeneratingArtifact(null);
          }
        }
      };
      
      pollArtifact();
      
    } catch (error) {
      console.error(`Erro ao regenerar ${artifactType}:`, error);
      toast.error(`Erro ao regenerar ${artifactType}`);
      setRegeneratingArtifact(null);
    }
  };

  const fetchCourseData = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      const [courseResponse, progressResponse] = await Promise.all([
        axios.get(`${API}/courses/${courseId}`, { headers }),
        axios.get(`${API}/progress/${courseId}`, { headers })
      ]);
      
      setCourse(courseResponse.data);
      setProgress(progressResponse.data);
      
      // Set first incomplete segment as active, or first segment if all complete
      const firstIncompleteSegment = courseResponse.data.segments.find(
        segment => !progressResponse.data.completed_segments.includes(segment.id)
      );
      setActiveSegment(firstIncompleteSegment || courseResponse.data.segments[0]);
      
    } catch (error) {
      console.error("Error fetching course data:", error);
      toast.error("Erro ao carregar dados do curso");
    } finally {
      setLoading(false);
    }
  };

  const markSegmentComplete = async (segmentId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.post(`${API}/progress`, {
        course_id: courseId,
        segment_id: segmentId,
        completed: true
      }, { headers });
      
      // Update local progress
      setProgress(prev => ({
        ...prev,
        completed_segments: [...prev.completed_segments, segmentId]
      }));
      
      // Recalculate completion percentage
      const totalSegments = course.segments.length;
      const completedCount = progress.completed_segments.length + 1;
      const newPercentage = (completedCount / totalSegments) * 100;
      
      setProgress(prev => ({
        ...prev,
        completion_percentage: newPercentage,
        is_completed: newPercentage >= 100
      }));
      
      toast.success("Módulo marcado como concluído!");
      
      if (newPercentage >= 100) {
        toast.success("🎉 Parabéns! Você concluiu o curso!");
        
        // Check if course requires assessment
        if (course.requires_assessment && course.linked_assessment_id) {
          setTimeout(() => {
            toast.info("Redirecionando para a avaliação...");
            navigate(`/assessment/${course.linked_assessment_id}`);
          }, 2000); // Wait 2 seconds to show success message
        }
      }
      
    } catch (error) {
      console.error("Error marking segment complete:", error);
      toast.error("Erro ao marcar módulo como concluído");
    }
  };

  const toggleSegmentExpansion = (segmentId) => {
    setExpandedSegments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(segmentId)) {
        newSet.delete(segmentId);
      } else {
        newSet.add(segmentId);
      }
      return newSet;
    });
  };

  const getSegmentStatus = (segmentId) => {
    if (!progress) return 'not-started';
    return progress.completed_segments.includes(segmentId) ? 'completed' : 'not-started';
  };

  const getSectorDisplayName = (sector) => {
    const sectorNames = {
      'estagio': 'Estágio',
      'tecnico': 'Técnico',
      'comercial': 'Comercial'
    };
    return sectorNames[sector] || sector;
  };

  const downloadCertificate = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(`${API}/certificates/course/${courseId}/download`, {
        headers,
        responseType: 'blob'
      });
      
      // Create blob URL and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Create temporary link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `Certificado_${course.title.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      
      toast.success("Certificado baixado com sucesso!");
      
    } catch (error) {
      console.error("Error downloading certificate:", error);
      toast.error("Erro ao baixar certificado");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner w-12 h-12 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando curso...</p>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="heading-secondary text-gray-600 mb-2">Curso não encontrado</h2>
          <p className="text-body text-gray-500 mb-4">
            O curso que você está procurando não existe ou você não tem acesso a ele.
          </p>
          <Link to="/dashboard">
            <Button data-testid="back-to-dashboard-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar ao Dashboard
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="navbar">
        <div className="container mx-auto px-6 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
            </Link>
            <div>
              <h1 className="nav-brand">{course.title}</h1>
              <p className="text-sm text-gray-600">
                {Math.round(progress?.completion_percentage || 0)}% concluído
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {progress?.is_completed && (
              <Badge className="badge badge-success">
                <Award className="w-4 h-4 mr-1" />
                Concluído
              </Badge>
            )}
            
            <a
              href={course.external_url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline"
              data-testid="external-link-btn"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Ver no YouTube
            </a>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <nav className="container mx-auto px-6 py-2 flex items-center gap-1 text-sm text-gray-500">
          <Link to="/dashboard" className="hover:text-blue-600 transition-colors">Dashboard</Link>
          <ChevronRight className="w-3 h-3 flex-shrink-0" />
          <span className="text-gray-800 font-medium truncate max-w-xs">{course.title}</span>
          {activeSegment && (
            <>
              <ChevronRight className="w-3 h-3 flex-shrink-0" />
              <span className="text-blue-600 truncate max-w-xs">{activeSegment.title}</span>
            </>
          )}
        </nav>
      </div>

      {/* Progress Bar */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Progresso do Curso</span>
            <span className="text-sm text-gray-600">
              {progress?.completed_segments?.length || 0} de {course.segments.length} módulos
            </span>
          </div>
          <Progress 
            value={progress?.completion_percentage || 0} 
            className="h-3"
            data-testid="course-progress-bar"
          />
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        {/* Floating Processing Indicator */}
        <FloatingProcessingIndicator
          segmentId={activeSegment?.id}
          token={token}
          isYouTubeUrl={isYouTubeUrl}
          processingArtifacts={regeneratingArtifact ? [regeneratingArtifact, ...processingQueue] : []}
          onDismiss={() => setShowFloatingIndicator(false)}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Course Info Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Course Details */}
            <Card className="card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-blue-600" />
                  Informações do Curso
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Descrição</h3>
                  <p className="text-sm text-gray-600">{course.description}</p>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Duração Total:</span>
                  <span className="font-medium">{course.total_duration_minutes} min</span>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Módulos:</span>
                  <span className="font-medium">{course.segments.length}</span>
                </div>
                
                <div>
                  <span className="text-sm text-gray-600">Setores:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {course.sectors.map(sector => (
                      <Badge key={sector} variant="secondary" className="text-xs">
                        {getSectorDisplayName(sector)}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Certificate Download Button */}
                {progress?.is_completed && (
                  <div className="pt-4 border-t">
                    {/* Show certificate button only if assessment was passed (if required) */}
                    {(!course.requires_assessment || progress.assessment_passed) ? (
                      <Button
                        onClick={downloadCertificate}
                        className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-semibold"
                        data-testid="download-certificate-btn"
                      >
                        <Award className="w-4 h-4 mr-2" />
                        Baixar Certificado
                      </Button>
                    ) : (
                      <div className="w-full p-3 bg-orange-50 border border-orange-200 rounded-lg text-center">
                        <p className="text-sm text-orange-700 font-medium">
                          📋 Complete a avaliação com nota mínima para obter o certificado
                        </p>
                        {course.linked_assessment_id && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-2 border-orange-500 text-orange-600 hover:bg-orange-500 hover:text-white"
                            onClick={() => navigate(`/assessment/${course.linked_assessment_id}`)}
                          >
                            Fazer Avaliação
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Course Segments List */}
            <Card className="card">
              <CardHeader>
                <CardTitle>Módulos do Curso</CardTitle>
                <CardDescription>
                  Clique nos módulos para visualizar o conteúdo
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {course.segments.map((segment, index) => {
                    const status = getSegmentStatus(segment.id);
                    const isActive = activeSegment?.id === segment.id;
                    const isExpanded = expandedSegments.has(segment.id);
                    
                    return (
                      <div key={segment.id} className="border rounded-lg overflow-hidden">
                        <div 
                          className={`p-3 cursor-pointer transition-colors ${
                            isActive ? 'bg-blue-50 border-blue-200' : 'bg-white hover:bg-gray-50'
                          }`}
                          onClick={() => {
                            setActiveSegment(segment);
                            toggleSegmentExpansion(segment.id);
                          }}
                          data-testid={`segment-${segment.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="flex-shrink-0">
                                {status === 'completed' ? (
                                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                                ) : (
                                  <PlayCircle className="w-5 h-5 text-gray-400" />
                                )}
                              </div>
                              
                              <div>
                                <h4 className="font-medium text-sm text-gray-500">
                                  {index + 1}. {segment.title}
                                </h4>
                                <div className="flex items-center gap-2 text-xs text-gray-600">
                                  <Clock className="w-3 h-3" />
                                  {segment.duration_minutes} min
                                </div>
                              </div>
                            </div>
                            
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            )}
                          </div>
                        </div>
                        
                        {isExpanded && (
                          <div className="p-3 bg-gray-50 border-t">
                            <p className="text-sm text-gray-600 mb-3">
                              {segment.description || 'Nenhuma descrição disponível'}
                            </p>
                            
                            <div className="flex gap-2">
                              <a
                                href={segment.video_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-primary btn-sm"
                                data-testid={`watch-segment-${segment.id}`}
                              >
                                <PlayCircle className="w-4 h-4 mr-1" />
                                Assistir
                              </a>
                              
                              {status !== 'completed' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    markSegmentComplete(segment.id);
                                  }}
                                  data-testid={`complete-segment-${segment.id}`}
                                >
                                  <CheckCircle2 className="w-4 h-4 mr-1" />
                                  Marcar como Concluído
                                </Button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-2">
            {activeSegment ? (
              <Card className="card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PlayCircle className="w-5 h-5 text-blue-600" />
                    {activeSegment.title}
                  </CardTitle>
                  <CardDescription className="flex items-center gap-4">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {activeSegment.duration_minutes} minutos
                    </span>
                    {getSegmentStatus(activeSegment.id) === 'completed' && (
                      <Badge className="badge badge-success">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Concluído
                      </Badge>
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Sistema de Abas - FASE 1: Artefatos de Vídeo */}
                  <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="grid w-full grid-cols-6 mb-6 bg-gray-100">
                      <TabsTrigger value="video" className="flex items-center gap-2 data-[state=active]:bg-[#2E75B6] data-[state=active]:text-white data-[state=active]:shadow-sm">
                        <PlayCircle className="w-4 h-4" />
                        <span className="hidden sm:inline">Vídeo</span>
                      </TabsTrigger>
                      <TabsTrigger value="mindmap" className="flex items-center gap-2 data-[state=active]:bg-[#2E75B6] data-[state=active]:text-white data-[state=active]:shadow-sm">
                        <Network className="w-4 h-4" />
                        <span className="hidden sm:inline">Mapa Mental</span>
                      </TabsTrigger>
                      <TabsTrigger value="flashcards" className="flex items-center gap-2 data-[state=active]:bg-[#2E75B6] data-[state=active]:text-white data-[state=active]:shadow-sm">
                        <CreditCard className="w-4 h-4" />
                        <span className="hidden sm:inline">Flashcards</span>
                      </TabsTrigger>
                      <TabsTrigger value="quiz" className="flex items-center gap-2 data-[state=active]:bg-[#2E75B6] data-[state=active]:text-white data-[state=active]:shadow-sm">
                        <ClipboardCheck className="w-4 h-4" />
                        <span className="hidden sm:inline">Quiz</span>
                      </TabsTrigger>
                      <TabsTrigger value="slides" className="flex items-center gap-2 data-[state=active]:bg-[#2E75B6] data-[state=active]:text-white data-[state=active]:shadow-sm">
                        <Presentation className="w-4 h-4" />
                        <span className="hidden sm:inline">Slides</span>
                      </TabsTrigger>
                      <TabsTrigger value="infographic" className="flex items-center gap-2 data-[state=active]:bg-[#2E75B6] data-[state=active]:text-white data-[state=active]:shadow-sm">
                        <BarChart3 className="w-4 h-4" />
                        <span className="hidden sm:inline">Infográfico</span>
                      </TabsTrigger>
                    </TabsList>

                    {/* Aba: Vídeo */}
                    <TabsContent value="video" className="space-y-6">
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">Descrição</h3>
                        <p className="text-gray-600 whitespace-pre-wrap">
                          {videoData?.description ||
                           activeSegment.description ||
                           'Nenhuma descrição disponível para este módulo.'}
                        </p>
                      </div>
                      
                      {/* Video Player */}
                      <VideoPlayer
                        videoUrl={activeSegment.video_url}
                        videoId={activeSegment.video_id}
                        title={activeSegment.title}
                        onComplete={() => markSegmentComplete(activeSegment.id)}
                      />
                    </TabsContent>

                    {/* Aba: Mapa Mental */}
                    <TabsContent value="mindmap">
                      {/* Progress Card */}
                      <ArtifactProgressCard
                        segmentId={activeSegment.id}
                        token={token}
                        isYouTubeUrl={isYouTubeUrl}
                        artifactType="mindMap"
                        isGenerating={regeneratingArtifact === 'mindMap'}
                      />

                      {videoArtifacts?.mindMap ? (
                        <div className="space-y-4">
                          <div className="flex justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={regeneratingArtifact !== null}
                              onClick={() => regenerateSpecificArtifact('mindMap')}
                              className="gap-2"
                            >
                              {regeneratingArtifact === 'mindMap' ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                  Regenerando...
                                </>
                              ) : (
                                <>🔄 Regenerar Mapa Mental</>
                              )}
                            </Button>
                          </div>
                          <MindMap mindMapData={videoArtifacts.mindMap} />
                        </div>
                      ) : regeneratingArtifact === 'mindMap' ? (
                        <Card className="card">
                          <CardContent className="p-8 text-center">
                            <div className="loading-spinner w-12 h-12 border-blue-600 mx-auto mb-4"></div>
                            <p className="text-gray-600 font-medium">Gerando Mapa Mental...</p>
                            <p className="text-sm text-gray-500 mt-2">
                              A IA está processando a transcrição do vídeo
                            </p>
                          </CardContent>
                        </Card>
                      ) : videoArtifacts && videoArtifacts.status === 'ERROR' ? (
                        <Card className="card border-red-200 bg-red-50">
                          <CardContent className="p-8 text-center">
                            <Network className="w-12 h-12 text-red-400 mx-auto mb-3" />
                            <p className="text-red-700 font-medium">Erro na Geração</p>
                            <p className="text-sm text-red-600 mt-2">
                              {videoArtifacts.error_message || 'Falha ao gerar mapa mental. Tente novamente.'}
                            </p>
                            <Button
                              onClick={() => regenerateSpecificArtifact('mindMap')}
                              disabled={regeneratingArtifact !== null}
                              className="gap-2 mt-4"
                              variant="outline"
                            >
                              {regeneratingArtifact === 'mindMap' ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                                  Tentando Novamente...
                                </>
                              ) : (
                                <>
                                  <RotateCcw className="w-4 h-4" />
                                  Tentar Novamente
                                </>
                              )}
                            </Button>
                          </CardContent>
                        </Card>
                      ) : (
                        <Card className="card">
                          <CardContent className="p-8 text-center">
                            <Network className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-600 font-medium mb-4">Mapa mental não gerado</p>
                            <Button
                              onClick={() => generateSpecificArtifactIfNotExists('mindMap')}
                              disabled={regeneratingArtifact !== null}
                              className="gap-2"
                            >
                              {regeneratingArtifact === 'mindMap' ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                  Gerando...
                                </>
                              ) : (
                                <>
                                  <Sparkles className="w-4 h-4" />
                                  Gerar Mapa Mental
                                </>
                              )}
                            </Button>
                          </CardContent>
                        </Card>
                      )}
                    </TabsContent>

                    {/* Aba: Flashcards */}
                    <TabsContent value="flashcards">
                      {/* Progress Card */}
                      <ArtifactProgressCard
                        segmentId={activeSegment.id}
                        token={token}
                        isYouTubeUrl={isYouTubeUrl}
                        artifactType="flashcards"
                        isGenerating={regeneratingArtifact === 'flashcards'}
                      />

                      {videoArtifacts?.flashcards ? (
                        <div className="space-y-4">
                          <div className="flex justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={regeneratingArtifact !== null}
                              onClick={() => regenerateSpecificArtifact('flashcards')}
                              className="gap-2"
                            >
                              {regeneratingArtifact === 'flashcards' ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                  Regenerando...
                                </>
                              ) : (
                                <>🔄 Regenerar Flashcards</>
                              )}
                            </Button>
                          </div>
                          <Flashcards flashcardsData={videoArtifacts.flashcards} />
                        </div>
                      ) : regeneratingArtifact === 'flashcards' ? (
                        <Card className="card">
                          <CardContent className="p-8 text-center">
                            <div className="loading-spinner w-12 h-12 border-purple-600 mx-auto mb-4"></div>
                            <p className="text-gray-600 font-medium">Gerando Flashcards...</p>
                            <p className="text-sm text-gray-500 mt-2">
                              A IA está criando perguntas e respostas
                            </p>
                          </CardContent>
                        </Card>
                      ) : videoArtifacts && videoArtifacts.status === 'ERROR' ? (
                        <Card className="card border-red-200 bg-red-50">
                          <CardContent className="p-8 text-center">
                            <CreditCard className="w-12 h-12 text-red-400 mx-auto mb-3" />
                            <p className="text-red-700 font-medium">Erro na Geração</p>
                            <p className="text-sm text-red-600 mt-2">
                              {videoArtifacts.error_message || 'Falha ao gerar flashcards. Tente novamente.'}
                            </p>
                            <Button
                              onClick={() => regenerateSpecificArtifact('flashcards')}
                              disabled={regeneratingArtifact !== null}
                              className="gap-2 mt-4"
                              variant="outline"
                            >
                              {regeneratingArtifact === 'flashcards' ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                                  Tentando Novamente...
                                </>
                              ) : (
                                <>
                                  <RotateCcw className="w-4 h-4" />
                                  Tentar Novamente
                                </>
                              )}
                            </Button>
                          </CardContent>
                        </Card>
                      ) : (
                        <Card className="card">
                          <CardContent className="p-8 text-center">
                            <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-600 font-medium mb-4">Flashcards não gerados</p>
                            <Button
                              onClick={() => generateSpecificArtifactIfNotExists('flashcards')}
                              disabled={regeneratingArtifact !== null}
                              className="gap-2"
                            >
                              {regeneratingArtifact === 'flashcards' ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                  Gerando...
                                </>
                              ) : (
                                <>
                                  <Sparkles className="w-4 h-4" />
                                  Gerar Flashcards
                                </>
                              )}
                            </Button>
                          </CardContent>
                        </Card>
                      )}
                    </TabsContent>

                    {/* Aba: Quiz */}
                    <TabsContent value="quiz">
                      {/* Progress Card */}
                      <ArtifactProgressCard
                        segmentId={activeSegment.id}
                        token={token}
                        isYouTubeUrl={isYouTubeUrl}
                        artifactType="quiz"
                        isGenerating={regeneratingArtifact === 'quiz'}
                      />

                      {videoArtifacts?.quiz ? (
                        <div className="space-y-4">
                          <div className="flex justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={regeneratingArtifact !== null}
                              onClick={() => regenerateSpecificArtifact('quiz')}
                              className="gap-2"
                            >
                              {regeneratingArtifact === 'quiz' ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                  Regenerando...
                                </>
                              ) : (
                                <>🔄 Regenerar Quiz</>
                              )}
                            </Button>
                          </div>
                          <Quiz quizData={videoArtifacts.quiz} />
                        </div>
                      ) : regeneratingArtifact === 'quiz' ? (
                        <Card className="card">
                          <CardContent className="p-8 text-center">
                            <div className="loading-spinner w-12 h-12 border-orange-600 mx-auto mb-4"></div>
                            <p className="text-gray-600 font-medium">Gerando Quiz...</p>
                            <p className="text-sm text-gray-500 mt-2">
                              A IA está preparando questões de verificação
                            </p>
                          </CardContent>
                        </Card>
                      ) : videoArtifacts && videoArtifacts.status === 'ERROR' ? (
                        <Card className="card border-red-200 bg-red-50">
                          <CardContent className="p-8 text-center">
                            <ClipboardCheck className="w-12 h-12 text-red-400 mx-auto mb-3" />
                            <p className="text-red-700 font-medium">Erro na Geração</p>
                            <p className="text-sm text-red-600 mt-2">
                              {videoArtifacts.error_message || 'Falha ao gerar quiz. Tente novamente.'}
                            </p>
                            <Button
                              onClick={() => regenerateSpecificArtifact('quiz')}
                              disabled={regeneratingArtifact !== null}
                              className="gap-2 mt-4"
                              variant="outline"
                            >
                              {regeneratingArtifact === 'quiz' ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                                  Tentando Novamente...
                                </>
                              ) : (
                                <>
                                  <RotateCcw className="w-4 h-4" />
                                  Tentar Novamente
                                </>
                              )}
                            </Button>
                          </CardContent>
                        </Card>
                      ) : (
                        <Card className="card">
                          <CardContent className="p-8 text-center">
                            <ClipboardCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-600 font-medium mb-4">Quiz não gerado</p>
                            <Button
                              onClick={() => generateSpecificArtifactIfNotExists('quiz')}
                              disabled={regeneratingArtifact !== null}
                              className="gap-2"
                            >
                              {regeneratingArtifact === 'quiz' ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                  Gerando...
                                </>
                              ) : (
                                <>
                                  <Sparkles className="w-4 h-4" />
                                  Gerar Quiz
                                </>
                              )}
                            </Button>
                          </CardContent>
                        </Card>
                      )}
                    </TabsContent>

                    {/* Aba: Slides */}
                    <TabsContent value="slides">
                      {/* Progress Card */}
                      <ArtifactProgressCard
                        segmentId={activeSegment.id}
                        token={token}
                        isYouTubeUrl={isYouTubeUrl}
                        artifactType="slides"
                        isGenerating={regeneratingArtifact === 'slides'}
                      />

                      {videoArtifacts?.slides ? (
                        <div className="space-y-4">
                          <div className="flex justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={regeneratingArtifact !== null}
                              onClick={() => regenerateSpecificArtifact('slides')}
                              className="gap-2"
                            >
                              {regeneratingArtifact === 'slides' ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                  Regenerando...
                                </>
                              ) : (
                                <>🔄 Regenerar Slides</>
                              )}
                            </Button>
                          </div>
                          <Slides slidesData={videoArtifacts.slides} />
                        </div>
                      ) : regeneratingArtifact === 'slides' ? (
                        <Card className="card">
                          <CardContent className="p-8 text-center">
                            <div className="loading-spinner w-12 h-12 border-indigo-600 mx-auto mb-4"></div>
                            <p className="text-gray-600 font-medium">Gerando Slides...</p>
                            <p className="text-sm text-gray-500 mt-2">
                              A IA está resumindo o conteúdo em slides
                            </p>
                          </CardContent>
                        </Card>
                      ) : videoArtifacts && videoArtifacts.status === 'ERROR' ? (
                        <Card className="card border-red-200 bg-red-50">
                          <CardContent className="p-8 text-center">
                            <Presentation className="w-12 h-12 text-red-400 mx-auto mb-3" />
                            <p className="text-red-700 font-medium">Erro na Geração</p>
                            <p className="text-sm text-red-600 mt-2">
                              {videoArtifacts.error_message}
                            </p>
                            <Button
                              onClick={() => regenerateSpecificArtifact('slides')}
                              disabled={regeneratingArtifact !== null}
                              className="gap-2 mt-4"
                              variant="destructive"
                            >
                              {regeneratingArtifact === 'slides' ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                  Tentando novamente...
                                </>
                              ) : (
                                <>🔄 Tentar Novamente</>
                              )}
                            </Button>
                          </CardContent>
                        </Card>
                      ) : (
                        <Card className="card">
                          <CardContent className="p-8 text-center">
                            <Presentation className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-600 font-medium mb-4">Slides não gerados</p>
                            <Button
                              onClick={() => generateSpecificArtifactIfNotExists('slides')}
                              disabled={regeneratingArtifact !== null}
                              className="gap-2"
                            >
                              {regeneratingArtifact === 'slides' ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                  Gerando...
                                </>
                              ) : (
                                <>
                                  <Sparkles className="w-4 h-4" />
                                  Gerar Slides
                                </>
                              )}
                            </Button>
                          </CardContent>
                        </Card>
                      )}
                    </TabsContent>

                    {/* Aba: Infográfico */}
                    <TabsContent value="infographic">
                      {/* Progress Card */}
                      <ArtifactProgressCard
                        segmentId={activeSegment.id}
                        token={token}
                        isYouTubeUrl={isYouTubeUrl}
                        artifactType="infographic"
                        isGenerating={regeneratingArtifact === 'infographic'}
                      />

                      {videoArtifacts?.infographic ? (
                        <div className="space-y-4">
                          <div className="flex justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={regeneratingArtifact !== null}
                              onClick={() => regenerateSpecificArtifact('infographic')}
                              className="gap-2"
                            >
                              {regeneratingArtifact === 'infographic' ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                  Regenerando...
                                </>
                              ) : (
                                <>🔄 Regenerar Infográfico</>
                              )}
                            </Button>
                          </div>
                          <Infographic infographicData={videoArtifacts.infographic} />
                        </div>
                      ) : regeneratingArtifact === 'infographic' ? (
                        <Card className="card">
                          <CardContent className="p-8 text-center">
                            <div className="loading-spinner w-12 h-12 border-blue-600 mx-auto mb-4"></div>
                            <p className="text-gray-600 font-medium">Gerando Infográfico...</p>
                            <p className="text-sm text-gray-500 mt-2">
                              A IA está criando visualizações e dados
                            </p>
                          </CardContent>
                        </Card>
                      ) : videoArtifacts && videoArtifacts.status === 'ERROR' ? (
                        <Card className="card border-red-200 bg-red-50">
                          <CardContent className="p-8 text-center">
                            <BarChart3 className="w-12 h-12 text-red-400 mx-auto mb-3" />
                            <p className="text-red-700 font-medium">Erro na Geração</p>
                            <p className="text-sm text-red-600 mt-2">
                              {videoArtifacts.error_message || 'Falha ao gerar infográfico. Tente novamente.'}
                            </p>
                            <Button
                              onClick={() => regenerateSpecificArtifact('infographic')}
                              disabled={regeneratingArtifact !== null}
                              className="gap-2 mt-4"
                              variant="outline"
                            >
                              {regeneratingArtifact === 'infographic' ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                                  Tentando Novamente...
                                </>
                              ) : (
                                <>
                                  <RotateCcw className="w-4 h-4" />
                                  Tentar Novamente
                                </>
                              )}
                            </Button>
                          </CardContent>
                        </Card>
                      ) : (
                        <Card className="card">
                          <CardContent className="p-8 text-center">
                            <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-600 font-medium mb-4">Infográfico não gerado</p>
                            <Button
                              onClick={() => generateSpecificArtifactIfNotExists('infographic')}
                              disabled={regeneratingArtifact !== null}
                              className="gap-2"
                            >
                              {regeneratingArtifact === 'infographic' ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                  Gerando...
                                </>
                              ) : (
                                <>
                                  <Sparkles className="w-4 h-4" />
                                  Gerar Infográfico
                                </>
                              )}
                            </Button>
                          </CardContent>
                        </Card>
                      )}
                    </TabsContent>
                  </Tabs>
                  
                  {/* Course completion status */}
                  {progress?.is_completed && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-6">
                      <div className="flex items-center gap-3">
                        <Award className="w-8 h-8 text-green-600" />
                        <div>
                          <h3 className="font-semibold text-green-900">
                            🎉 Curso Concluído!
                          </h3>
                          <p className="text-sm text-green-700">
                            Parabéns! Você concluiu todos os módulos deste curso. 
                            Seu certificado foi gerado automaticamente.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="card text-center py-12">
                <CardContent>
                  <PlayCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="heading-tertiary text-gray-600 mb-2">
                    Selecione um módulo
                  </h3>
                  <p className="text-body text-gray-500">
                    Clique em um dos módulos na barra lateral para começar a assistir.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default CourseViewer;